import { ReactElement, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import DialogBox2 from '@/components/DialogBox2';
import Constants from '@/Constants';
import useDialog from '@/hooks/util/useDialog';
import useAdminLogin from '@/pages/AdminLogin/hooks/useAdminLogin';
import useStore from '@/store';

export default function AppInfoDialog(): ReactElement {
  const { isOpen, closeDialog, updateDialogProperties } = useDialog({ open: false });
  const { adminDetails } = useAdminLogin();
  const { appVersion } = useStore();
  const onClose = () => {
    closeDialog();
  };
  useEffect(() => {
    const cleanup = window.electronAPI?.onShowAppInfo(() => {
      updateDialogProperties({ open: true });
    });
    return () => {
      cleanup();
    };
  }, [updateDialogProperties]);
  const { t } = useTranslation(['adminLogin', 'translation']);
  const lines = useMemo(() => {
    return [
      { title: t('adminLogin:adminCode'), value: adminDetails?.adminCode || '' },
      { title: t('translation:schoolName'), value: adminDetails?.centerName || '' },
      { title: t('translation:schoolCode'), value: adminDetails?.centerCode || '' },
      { title: t('translation:programCode'), value: adminDetails?.programCode || '' },
      { title: t('adminLogin:deviceId'), value: localStorage.getItem('device-id') || '' },
      { title: t('adminLogin:appVersion'), value: `${appVersion}/${Constants.GIT_INFO}` },
    ];
  }, [adminDetails, appVersion, t]);
  const getDialogBoxComponent = () => {
    return (
      <div className="w-full min-w-[300px] rounded-2xl rounded-l-2xl bg-white px-4 pb-8 pt-8">
        <h2 className="mb-5 text-[1.1875em] font-[600] tracking-[0.01em] text-neutral-grey-4">
          {t('adminLogin:adminDetails')}
        </h2>
        <div className="grid grid-cols-2 gap-y-3">
          {lines.map((line) =>
            line.value ? (
              <>
                <p className="text-[0.75em] font-[500] text-neutral-grey-3">{line.title}</p>
                <p className="text-[0.9375em] font-[600] tracking-tight text-neutral-grey-4">
                  {line.value}
                </p>
              </>
            ) : undefined,
          )}
        </div>
      </div>
    );
  };
  return isOpen ? (
    <div className="absolute top-0 z-50 h-screen w-screen">
      <DialogBox2
        open={isOpen}
        onClose={onClose}
        styles="!px-0 !max-w-[838px] !py-0"
        showCloseIcon
        dismissable
      >
        {getDialogBoxComponent()}
      </DialogBox2>
    </div>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <></>
  );
}
